import { ChevronDown, ChevronRight } from 'lucide-react'
import { useRef, type KeyboardEvent, type ReactNode } from 'react'

import './browse.css'

/** Узел дерева, уже развёрнутого в плоский список видимых строк (как `StructureNodeVm`): глубина задаёт отступ. */
export interface TreeNavNode {
  id: string
  label: string
  /** Вторая строка: тип узла или пояснение («3 кассы», «магазин»). */
  caption?: string | null
  depth: number
  expandable: boolean
  expanded: boolean
  selected: boolean
  /** Число справа («12 касс»); пусто — без числа. */
  badge?: string | null
  icon?: ReactNode
}

/**
 * Вертикальное дерево выбора (WAI-ARIA tree): стрелки вверх/вниз ходят по строкам, вправо раскрывает или входит в
 * узел, влево сворачивает или поднимается к родителю, Enter/пробел выбирает. Выбранный узел отличается рамкой слева,
 * жирностью и `aria-selected`, а не только цветом (CLAUDE.md §7). Что узел значит — решает вызывающий: компонент не
 * знает ни организаций, ни касс.
 */
export function TreeNav({
  label,
  nodes,
  onSelect,
  onToggle,
}: {
  label: string
  nodes: readonly TreeNavNode[]
  onSelect: (id: string) => void
  onToggle: (id: string) => void
}) {
  const refs = useRef(new Map<string, HTMLDivElement>())
  const focusable = nodes.find((node) => node.selected)?.id ?? nodes[0]?.id

  const focus = (id: string | undefined) => {
    if (id !== undefined) refs.current.get(id)?.focus()
  }

  const onKey = (event: KeyboardEvent<HTMLDivElement>, index: number) => {
    const node = nodes[index]
    if (node === undefined) return
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        focus(nodes[index + 1]?.id)
        break
      case 'ArrowUp':
        event.preventDefault()
        focus(nodes[index - 1]?.id)
        break
      case 'ArrowRight':
        event.preventDefault()
        if (node.expandable && !node.expanded) onToggle(node.id)
        else if (node.expandable) focus(nodes[index + 1]?.id)
        break
      case 'ArrowLeft': {
        event.preventDefault()
        if (node.expandable && node.expanded) {
          onToggle(node.id)
          break
        }
        const parent = [...nodes.slice(0, index)].reverse().find((candidate) => candidate.depth < node.depth)
        focus(parent?.id)
        break
      }
      case 'Home':
        event.preventDefault()
        focus(nodes[0]?.id)
        break
      case 'End':
        event.preventDefault()
        focus(nodes[nodes.length - 1]?.id)
        break
      case 'Enter':
      case ' ':
        event.preventDefault()
        onSelect(node.id)
        break
      default:
    }
  }

  return (
    <ul className="krp-tree" role="tree" aria-label={label}>
      {nodes.map((node, index) => (
        <li key={node.id} role="none">
          <div
            ref={(element) => {
              if (element === null) refs.current.delete(node.id)
              else refs.current.set(node.id, element)
            }}
            role="treeitem"
            tabIndex={node.id === focusable ? 0 : -1}
            aria-level={node.depth + 1}
            aria-selected={node.selected}
            aria-expanded={node.expandable ? node.expanded : undefined}
            className="krp-tree__row"
            data-selected={node.selected || undefined}
            style={{ paddingInlineStart: `calc(var(--krp-sp-2) + ${node.depth} * var(--krp-sp-4))` }}
            onClick={() => onSelect(node.id)}
            onKeyDown={(event) => onKey(event, index)}
          >
            {node.expandable ? (
              <button
                type="button"
                tabIndex={-1}
                className="krp-tree__toggle"
                aria-label={`${node.expanded ? 'Свернуть' : 'Развернуть'} «${node.label}»`}
                onClick={(event) => {
                  event.stopPropagation()
                  onToggle(node.id)
                }}
              >
                {node.expanded ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
              </button>
            ) : (
              <span className="krp-tree__toggle" aria-hidden="true" />
            )}
            {node.icon !== undefined && <span className="krp-tree__icon">{node.icon}</span>}
            <span className="krp-tree__text">
              <span className="krp-tree__label">{node.label}</span>
              {node.caption != null && node.caption !== '' && <span className="krp-tree__caption">{node.caption}</span>}
            </span>
            {node.badge != null && node.badge !== '' && <span className="krp-tree__badge">{node.badge}</span>}
          </div>
        </li>
      ))}
    </ul>
  )
}
